import { useState } from 'react';
import { Mail, Phone, Edit, Trash2, Send, User } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import EditGuestDialog from './EditGuestDialog';
import { toast } from 'sonner';

export const GuestCard = ({ guest, onUpdate, onDelete, onSendAccommodation, hotelVendors = [], existingGuests = [], canEdit = true, canDelete = true, isReadOnly = false }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [sendingAccommodation, setSendingAccommodation] = useState(false);

  const getRSVPBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-success/10 text-success border-success/20">Confirmed</Badge>;
      case 'pending':
        return <Badge className="bg-warning/10 text-warning border-warning/20">Pending</Badge>;
      case 'declined':
        return <Badge className="bg-destructive/10 text-destructive border-destructive/20">Declined</Badge>;
      case 'maybe':
        return <Badge className="bg-muted text-muted-foreground">Maybe</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  const getSideBadge = (side) => {
    const colors = {
      'Bride Side': 'bg-pink-500/10 text-pink-600 border-pink-500/20',
      'Groom Side': 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      'VIP': 'gradient-gold text-foreground',
      'Relatives': 'bg-purple-500/10 text-purple-600 border-purple-500/20',
      'Friends': 'bg-teal-500/10 text-teal-600 border-teal-500/20'
    };
    return <Badge className={colors[side] || 'bg-muted'}>{side}</Badge>;
  };

  const handleSendInvite = () => {
    toast.success(`Invitation sent to ${guest.name}!`);
  };

  const handleSendAccommodation = async () => {
    if (sendingAccommodation) return;
    
    setSendingAccommodation(true);
    try {
      await onSendAccommodation(guest.id);
      toast.success(`Accommodation details sent to ${guest.name}!`);
    } catch (error) {
      toast.error(error.message || 'Failed to send accommodation details');
    } finally {
      setSendingAccommodation(false);
    }
  };

  const handleDelete = () => {
    onDelete(guest.id);
    setShowDeleteDialog(false);
  };

  return (
    <>
      <Card className="p-5 transition-smooth hover:shadow-medium">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="p-2 rounded-full bg-primary/10 shrink-0">
              <User className="h-4 w-4 text-primary" />
            </div>
            <h3 className="font-bold text-lg truncate">{guest.name}</h3>
          </div>
          {getSideBadge(guest.side)}
        </div>

        {/* Contact Info */}
        <div className="space-y-2 mb-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mail className="h-3.5 w-3.5" />
            <span className="truncate">{guest.email}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="h-3.5 w-3.5" />
            <span>{guest.phone}</span>
          </div>
        </div>

        <div className="border-t border-border my-3"></div>

        {/* Details */}
        <div className="space-y-2 mb-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">RSVP:</span>
            {getRSVPBadge(guest.rsvpStatus)}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Guest Count:</span>
            <span className="font-semibold">
              {isNaN(guest.guestCount) || !guest.guestCount ? '1' : guest.guestCount} {(isNaN(guest.guestCount) || !guest.guestCount || guest.guestCount === 1) ? 'person' : 'people'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Meal:</span>
            <span className="font-medium">{guest.mealPreference}</span>
          </div>
          {guest.roomAllocation && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Room:</span>
              <span className="font-medium">{guest.roomAllocation}</span>
            </div>
          )}
        </div>

        {/* Accommodation Details */}
        {guest.hotelName && (
          <>
            <div className="border-t border-border my-3"></div>
            <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-3 mb-3 space-y-1.5">
              <h4 className="text-xs font-semibold text-amber-900 dark:text-amber-100 flex items-center gap-1">
                🏨 Accommodation
              </h4>
              <p className="text-xs text-amber-800 dark:text-amber-200">
                <strong>Hotel:</strong> {guest.hotelName}
              </p>
              {guest.numberOfRooms && (
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  <strong>Rooms:</strong> {guest.numberOfRooms}
                </p>
              )}
              {guest.roomNumbers && (
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  <strong>Room Numbers:</strong> {guest.roomNumbers}
                </p>
              )}
              {guest.checkInDate && guest.checkOutDate && (
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  <strong>Stay:</strong> {guest.checkInDate} to {guest.checkOutDate}
                </p>
              )}
            </div>
          </>
        )}

        {/* Notes */}
        {guest.notes && (
          <>
            <div className="border-t border-border my-3"></div>
            <div className="bg-muted/30 rounded-lg p-3 mb-3">
              <p className="text-xs text-muted-foreground line-clamp-2">📝 {guest.notes}</p>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 pt-3 border-t border-border">
          <div className="flex gap-2">
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEditDialog(true)}
                className="flex-1"
                disabled={isReadOnly}
              >
                <Edit className="h-3.5 w-3.5" />
                Edit
              </Button>
            )}
            {canDelete && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendInvite}
                  className="flex-1"
                >
                  <Send className="h-3.5 w-3.5" />
                  Invite
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={isReadOnly}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
          
          {/* Send Accommodation Button - Only show if accommodation details exist */}
          {canDelete && guest.hotelName && guest.roomNumber && (guest.email || guest.phone) && (
            <Button
              variant="default"
              size="sm"
              onClick={() => handleSendAccommodation()}
              disabled={sendingAccommodation}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
            >
              {sendingAccommodation ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5 mr-2" />
                  Send Accommodation Details
                </>
              )}
            </Button>
          )}
        </div>
      </Card>

      {/* Edit Dialog */}
      <EditGuestDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        guest={guest}
        onUpdateGuest={onUpdate}
        hotelVendors={hotelVendors}
        existingGuests={existingGuests}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Guest?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{guest.name}</strong> from the guest list?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Guest
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GuestCard;